// src/components/IdeaDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Pencil, Trash2, Download, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import Collapsible from './Collapsible';
import StarRating from './StarRating';
import SubtaskList from './SubtaskList';
import IdeaForm from './IdeaForm';
import ResourcesSection from './ResourcesSection';
import ExportModal from './ExportModal';
import { Link } from 'react-router-dom';
import { normalizeUrl } from '../utils/utils';
import { useEntitlementSummary } from '../context/EntitlementSummaryContext';
import {
  evaluateEnhancementEntitlementHint,
  getEntitlementFooterHint,
} from '../utils/enhancementEntitlementHint';

// Color name mapping
const COLOR_NAMES = {
  '#3B82F6': 'Blue',
  '#10B981': 'Green',
  '#F59E0B': 'Amber',
  '#EF4444': 'Red',
  '#8B5CF6': 'Purple',
  '#EC4899': 'Pink',
  '#06B6D4': 'Cyan'
};

const getColorName = (hexColor) => {
  return COLOR_NAMES[hexColor] || hexColor;
};

function IdeaDetailModal({ 
  idea, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete, 
  onDevelop, // New prop for re-development flow
  tags, 
  user,
  setHasUnsavedChanges 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [localIdea, setLocalIdea] = useState(idea);
  const {
    summary: entitlementSummary,
    loading: entitlementLoading,
    error: entitlementError,
  } = useEntitlementSummary();
  const enhancementEntitlement = evaluateEnhancementEntitlementHint({
    summary: entitlementSummary,
  });
  const canUseEnhancement = enhancementEntitlement.allowed;
  const developEntitlementHint = getEntitlementFooterHint({
    loading: entitlementLoading,
    summary: entitlementSummary,
    allowed: canUseEnhancement,
    snapshotError: entitlementError,
  });
  const [editForm, setEditForm] = useState({
    idea: idea?.idea || '',
    description: idea?.description || '',
    tags: idea?.tags || [],
    status: idea?.status || 'Drafted',
    rating: idea?.rating || 0,
    subtasks: idea?.subtasks || [],
    lessonsLearned: idea?.lessonsLearned || '',
    resources: idea?.resources || [],
    color: idea?.color || null
  });

  // Update local idea when prop changes
  useEffect(() => {
    if (idea) {
      setLocalIdea(idea);
      setEditForm({
        idea: idea.idea || '',
        description: idea.description || '',
        tags: idea.tags || [],
        status: idea.status || 'Drafted',
        rating: idea.rating || 0,
        subtasks: idea.subtasks || [],
        lessonsLearned: idea.lessonsLearned || '',
        resources: idea.resources || [],
        color: idea.color || null
      });
    }
  }, [idea]);


  // Don't return null if we're showing a notification - keep it visible
  if (!isOpen || !idea) return null;

  const handleEditClick = () => {
    setEditForm({
      idea: idea.idea,
      description: idea.description,
      tags: idea.tags || [],
      status: idea.status || 'Drafted',
      rating: idea.rating || 0,
      subtasks: idea.subtasks || [],
      lessonsLearned: idea.lessonsLearned || '',
      resources: idea.resources || [],
      color: idea.color || null
    });
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    await onSave(idea.id, editForm);
    setIsEditing(false);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      idea: idea.idea,
      description: idea.description,
      tags: idea.tags || [],
      status: idea.status || 'Drafted',
      rating: idea.rating || 0,
      subtasks: idea.subtasks || [],
      lessonsLearned: idea.lessonsLearned || ''
    });
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this idea?')) {
      onDelete(idea.id);
      onClose();
    }
  };

  const handleAddResource = (label, url) => {
    // Normalize URL to ensure it has a protocol
    const normalizedUrl = normalizeUrl(url);
    
    try {
      const urlObj = new URL(normalizedUrl);
      const defaultLabel = urlObj.hostname.replace('www.', '');
      const newResource = {
        id: Date.now().toString(),
        label: label || defaultLabel,
        url: normalizedUrl
      };
      const updatedResources = [...(localIdea?.resources || idea?.resources || []), newResource];
      setLocalIdea(prev => ({ ...(prev || idea), resources: updatedResources }));
      // Save immediately
      if (onSave && (localIdea?.id || idea?.id)) {
        onSave(localIdea?.id || idea?.id, { ...(localIdea || idea), resources: updatedResources });
      }
    } catch (error) {
      // If URL parsing fails, use the URL as label
      const newResource = {
        id: Date.now().toString(),
        label: label || normalizedUrl,
        url: normalizedUrl
      };
      const updatedResources = [...(localIdea?.resources || idea?.resources || []), newResource];
      setLocalIdea(prev => ({ ...(prev || idea), resources: updatedResources }));
      // Save immediately
      if (onSave && (localIdea?.id || idea?.id)) {
        onSave(localIdea?.id || idea?.id, { ...(localIdea || idea), resources: updatedResources });
      }
    }
  };

  const handleDeleteResource = (resourceId) => {
    const updatedResources = (localIdea?.resources || idea?.resources || []).filter(r => r.id !== resourceId);
    setLocalIdea(prev => ({ ...(prev || idea), resources: updatedResources }));
    // Save immediately
    if (onSave && (localIdea?.id || idea?.id)) {
      onSave(localIdea?.id || idea?.id, { ...(localIdea || idea), resources: updatedResources });
    }
  };

  return (
    <>
      {/* Mobile: Bottom Sheet */}
      <div className="md:hidden fixed inset-0 z-50 flex items-end">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        {/* Bottom Sheet */}
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl w-full max-h-[90vh] flex flex-col relative z-10 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Idea' : localIdea?.idea || idea?.idea}
            </h2>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  {/* Develop Button */}
                  {onDevelop && (
                    <button
                      onClick={() => onDevelop(localIdea || idea)}
                      disabled={!canUseEnhancement || entitlementLoading}
                      className={`p-2 rounded transition-colors ${
                        canUseEnhancement && !entitlementLoading
                          ? 'text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                          : 'text-gray-400 cursor-not-allowed opacity-50'
                      }`}
                      aria-label="Develop with AI"
                      title={
                        !canUseEnhancement || entitlementLoading
                          ? developEntitlementHint || "AI enhancement isn't available for your account right now. See Support."
                          : 'Develop with AI'
                      }
                    >
                      <Sparkles className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="p-2 rounded transition-colors text-primary hover:bg-primary/10"
                    aria-label="Export to Cursor"
                    title="Export to Cursor (templates and copy)"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleEditClick}
                    className="p-2 text-primary hover:bg-primary/10 rounded transition-colors"
                    aria-label="Edit idea"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                    aria-label="Delete idea"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isEditing ? (
              <IdeaForm
                formData={editForm}
                setFormData={setEditForm}
                handleSubmit={handleSave}
                handleCancel={handleCancel}
              />
            ) : (
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Collapsible title="Description" defaultExpanded={false}>
                    <div className="markdown-preview text-gray-700 dark:text-gray-300">
                      {(localIdea?.description || idea?.description) ? (
                        <ReactMarkdown
                          components={{
                            h1: ({...props}) => <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2" {...props} />,
                            h2: ({...props}) => <h2 className="text-xl font-bold mb-3 mt-5 text-gray-900 dark:text-white" {...props} />,
                            h3: ({...props}) => <h3 className="text-lg font-semibold mb-2 mt-4 text-gray-900 dark:text-white" {...props} />,
                            h4: ({...props}) => <h4 className="text-base font-semibold mb-2 mt-3 text-gray-900 dark:text-white" {...props} />,
                            p: ({...props}) => <p className="mb-3 text-gray-700 dark:text-gray-300 leading-relaxed" {...props} />,
                            ul: ({...props}) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                            ol: ({...props}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                            li: ({...props}) => <li className="ml-4" {...props} />,
                            code: ({inline, ...props}) => 
                              inline ? (
                                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200" {...props} />
                              ) : (
                                <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />
                              ),
                            pre: ({...props}) => <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />,
                            blockquote: ({...props}) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-3 text-gray-600 dark:text-gray-400" {...props} />,
                            hr: ({...props}) => <hr className="my-6 border-gray-300 dark:border-gray-700" {...props} />,
                            strong: ({...props}) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
                            em: ({...props}) => <em className="italic" {...props} />,
                            a: ({...props}) => <a className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
                          }}
                        >
                          {localIdea?.description || idea?.description}
                        </ReactMarkdown>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">No description provided</span>
                      )}
                    </div>
                  </Collapsible>
                </div>

                {/* Tags */}
                {(() => {
                  const ideaTags = localIdea?.tags || idea?.tags || [];
                  return Array.isArray(ideaTags) && ideaTags.length > 0;
                })() && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(localIdea?.tags || idea?.tags || []).map((tagId) => (
                        <Link
                          key={tagId}
                          to={`/?tag=${encodeURIComponent(tags[tagId])}`}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                        >
                          {tags[tagId]}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resources */}
                <ResourcesSection
                  resources={localIdea?.resources || idea?.resources || []}
                  onAddResource={handleAddResource}
                  onDeleteResource={handleDeleteResource}
                />

                {/* Status, Rating, and Color */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      (localIdea?.status || idea?.status) === 'Completed' 
                        ? 'bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : (localIdea?.status || idea?.status) === 'In Progress'
                        ? 'bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        : (localIdea?.status || idea?.status) === 'Paused'
                        ? 'bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : (localIdea?.status || idea?.status) === 'Archived'
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {localIdea?.status || idea?.status}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rating
                    </h3>
                    <StarRating value={localIdea?.rating || idea?.rating} readOnly={true} />
                  </div>
                  {(localIdea?.color || idea?.color) && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Color
                      </h3>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-md border-2 border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: localIdea?.color || idea?.color }}
                          title={getColorName(localIdea?.color || idea?.color)}
                          aria-label={`Color: ${getColorName(localIdea?.color || idea?.color)}`}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {getColorName(localIdea?.color || idea?.color)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lessons Learned - Show when status is Archived or Paused */}
                {((localIdea?.status || idea?.status) === 'Archived' || (localIdea?.status || idea?.status) === 'Paused') && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Lessons Learned
                    </h3>
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {(localIdea?.lessonsLearned || idea?.lessonsLearned) || 'No lessons learned captured yet.'}
                    </div>
                  </div>
                )}

                {/* TODOs - Hide when status is Archived or Paused */}
                {((localIdea?.status || idea?.status) !== 'Archived' && (localIdea?.status || idea?.status) !== 'Paused') && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      TODOs
                    </h3>
                    <SubtaskList
                      subtasks={localIdea?.subtasks || idea?.subtasks || []}
                      setSubtasks={async (newSubtasks) => {
                        // Update local state for immediate UI update
                        setLocalIdea(prev => {
                          const updated = { ...(prev || idea), subtasks: newSubtasks };
                          // Also update parent state so changes persist when modal closes
                          if (onSave && (localIdea?.id || idea?.id)) {
                            onSave(localIdea?.id || idea?.id, updated).catch(err => {
                              console.error('Error syncing subtask update to parent:', err);
                            });
                          }
                          return updated;
                        });
                      }}
                      ideaId={localIdea?.id || idea?.id}
                      user={user}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: Centered Modal */}
      <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col relative z-10">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Idea' : (localIdea?.idea || idea?.idea)}
            </h2>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <>
                  {/* Develop Button */}
                  {onDevelop && (
                    <button
                      onClick={() => onDevelop(localIdea || idea)}
                      disabled={!canUseEnhancement || entitlementLoading}
                      className={`p-2 rounded transition-colors ${
                        canUseEnhancement && !entitlementLoading
                          ? 'text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                          : 'text-gray-400 cursor-not-allowed opacity-50'
                      }`}
                      aria-label="Develop with AI"
                      title={
                        !canUseEnhancement || entitlementLoading
                          ? developEntitlementHint || "AI enhancement isn't available for your account right now. See Support."
                          : 'Develop with AI'
                      }
                    >
                      <Sparkles className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowExportModal(true)}
                    className="p-2 rounded transition-colors text-primary hover:bg-primary/10"
                    aria-label="Export to Cursor"
                    title="Export to Cursor (templates and copy)"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleEditClick}
                    className="p-2 text-primary hover:bg-primary/10 rounded transition-colors"
                    aria-label="Edit idea"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                    aria-label="Delete idea"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isEditing ? (
              <IdeaForm
                formData={editForm}
                setFormData={setEditForm}
                handleSubmit={handleSave}
                handleCancel={handleCancel}
              />
            ) : (
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <Collapsible title="Description" defaultExpanded={false}>
                    <div className="markdown-preview text-gray-700 dark:text-gray-300">
                      {(localIdea || idea)?.description ? (
                        <ReactMarkdown
                          components={{
                            h1: ({...props}) => <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2" {...props} />,
                            h2: ({...props}) => <h2 className="text-xl font-bold mb-3 mt-5 text-gray-900 dark:text-white" {...props} />,
                            h3: ({...props}) => <h3 className="text-lg font-semibold mb-2 mt-4 text-gray-900 dark:text-white" {...props} />,
                            h4: ({...props}) => <h4 className="text-base font-semibold mb-2 mt-3 text-gray-900 dark:text-white" {...props} />,
                            p: ({...props}) => <p className="mb-3 text-gray-700 dark:text-gray-300 leading-relaxed" {...props} />,
                            ul: ({...props}) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                            ol: ({...props}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                            li: ({...props}) => <li className="ml-4" {...props} />,
                            code: ({inline, ...props}) => 
                              inline ? (
                                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200" {...props} />
                              ) : (
                                <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />
                              ),
                            pre: ({...props}) => <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />,
                            blockquote: ({...props}) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-3 text-gray-600 dark:text-gray-400" {...props} />,
                            hr: ({...props}) => <hr className="my-6 border-gray-300 dark:border-gray-700" {...props} />,
                            strong: ({...props}) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
                            em: ({...props}) => <em className="italic" {...props} />,
                            a: ({...props}) => <a className="text-primary hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
                          }}
                        >
                          {(localIdea || idea)?.description}
                        </ReactMarkdown>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">No description provided</span>
                      )}
                    </div>
                  </Collapsible>
                </div>

                {/* Tags */}
                {(() => {
                  const ideaTags = localIdea?.tags || idea?.tags || [];
                  return Array.isArray(ideaTags) && ideaTags.length > 0;
                })() && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(localIdea?.tags || idea?.tags || []).map((tagId) => (
                        <Link
                          key={tagId}
                          to={`/?tag=${encodeURIComponent(tags[tagId])}`}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                        >
                          {tags[tagId]}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resources */}
                <ResourcesSection
                  resources={(localIdea || idea)?.resources || []}
                  onAddResource={handleAddResource}
                  onDeleteResource={handleDeleteResource}
                />

                {/* Status, Rating, and Color */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      (localIdea || idea)?.status === 'Completed' 
                        ? 'bg-green-200 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : (localIdea || idea)?.status === 'In Progress'
                        ? 'bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        : (localIdea || idea)?.status === 'Paused'
                        ? 'bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                        : (localIdea || idea)?.status === 'Archived'
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {(localIdea || idea)?.status}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rating
                    </h3>
                    <StarRating value={(localIdea || idea)?.rating} readOnly={true} />
                  </div>
                  {((localIdea || idea)?.color) && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Color
                      </h3>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-md border-2 border-gray-300 dark:border-gray-600"
                          style={{ backgroundColor: (localIdea || idea)?.color }}
                          title={getColorName((localIdea || idea)?.color)}
                          aria-label={`Color: ${getColorName((localIdea || idea)?.color)}`}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {getColorName((localIdea || idea)?.color)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lessons Learned - Show when status is Archived or Paused */}
                {((localIdea || idea)?.status === 'Archived' || (localIdea || idea)?.status === 'Paused') && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Lessons Learned
                    </h3>
                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {(localIdea || idea)?.lessonsLearned || 'No lessons learned captured yet.'}
                    </div>
                  </div>
                )}

                {/* TODOs - Hide when status is Archived or Paused */}
                {((localIdea || idea)?.status !== 'Archived' && (localIdea || idea)?.status !== 'Paused') && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      TODOs
                    </h3>
                    <SubtaskList
                      subtasks={(localIdea || idea)?.subtasks || []}
                      setSubtasks={async (newSubtasks) => {
                        // Update local state for immediate UI update
                        setLocalIdea(prev => {
                          const updated = { ...(prev || idea), subtasks: newSubtasks };
                          // Also update parent state so changes persist when modal closes
                          if (onSave && (localIdea?.id || idea?.id)) {
                            onSave(localIdea?.id || idea?.id, updated).catch(err => {
                              console.error('Error syncing subtask update to parent:', err);
                            });
                          }
                          return updated;
                        });
                      }}
                      ideaId={(localIdea || idea)?.id}
                      user={user}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        idea={localIdea || idea}
        tags={tags}
      />
    </>
  );
}

export default IdeaDetailModal;

